import { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, protectedPost, streamChat } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { chatMessage, useChatMessages } from "./useChatMessages";

const IMAGE_RE = /^(?:please\s+)?(?:jarvis[,\s]+)?(?:generate|create|draw|make|render|paint|produce)\s+(?:me\s+)?(?:an?\s+|the\s+)?(?:image|picture|photo|illustration|artwork|drawing|painting|render)\s*(?:of|:|-|showing|depicting|for)?\s*(.*)$/i;

async function renderImage(prompt, workspace, assistant, patch) {
  if (!prompt) {
    patch(assistant.id, { content: "Certainly, Sir — what shall I render? e.g. “generate image of a chrome arc reactor”." });
    return;
  }
  patch(assistant.id, { content: "Rendering your image, Sir — this can take up to a minute…" });
  const image = await protectedPost("/images/generate", { prompt, workspace });
  patch(assistant.id, { content: "Rendered, Sir. I've filed it under Files → Generated.", image });
}

async function findCommand(content) {
  try { return (await apiPost("/commands/match", { text: content })).match; }
  catch { throw new Error("Could not check saved commands. Nothing was executed; please retry."); }
}

async function streamAnswer({ history, macro, assistant, webMode, caps, patch, delta, state, signal, sessionId }) {
  const messages = history.filter((m) => m.role !== "system").map(({ role, content }) => ({ role, content }));
  if (macro?.action_type === "prompt" && macro.payload) messages[messages.length - 1] = { role: "user", content: macro.payload };
  state?.("thinking");
  let first = true;
  await streamChat({ messages, sessionId, signal, provider: webMode && caps.perplexity ? "perplexity" : undefined,
    onDelta: (text) => { if (first) { state?.("speaking"); first = false; } delta(assistant.id, text); },
    onCitations: (urls) => patch(assistant.id, { citations: urls }),
    onError: (message) => patch(assistant.id, { content: `Request failed: ${message}` }),
  });
}

function useBriefingSchedule(runBriefing, busy) {
  const latest = useRef({ runBriefing, busy });
  useEffect(() => { latest.current = { runBriefing, busy }; }, [runBriefing, busy]);
  useEffect(() => {
    let settings = { enabled: false }, lastDay = null, active = true;
    const refresh = () => apiGet("/briefing/settings").then((data) => { if (active) settings = data; })
      .catch(() => { settings = { enabled: false }; console.warn("Briefing schedule could not be refreshed."); });
    const tick = () => {
      const now = new Date(), day = now.toDateString();
      const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      if (!settings.enabled || latest.current.busy || lastDay === day || time !== settings.time) return;
      lastDay = day; latest.current.runBriefing();
    };
    refresh();
    const refreshTimer = setInterval(refresh, 60000), tickTimer = setInterval(tick, 20000);
    return () => { active = false; clearInterval(refreshTimer); clearInterval(tickTimer); };
  }, []);
}

export function useChatActions(onStateChange) {
  const { messages, append, patch, delta } = useChatMessages();
  const [input, setInput] = useState(""), [files, setFiles] = useState([]), [busy, setBusy] = useState(false);
  const [webMode, setWebMode] = useState(false), [caps, setCaps] = useState({ perplexity: false, image: false });
  const [capabilityError, setCapabilityError] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const busyRef = useRef(false), controller = useRef(null), active = useRef(true);
  const { workspace } = useWorkspace();
  useEffect(() => {
    active.current = true;
    apiGet("/system/status").then((s) => { if (active.current) setCaps({ perplexity: !!s.providers?.perplexity, image: !!s.image_generation }); })
      .catch(() => { if (active.current) setCapabilityError(true); });
    return () => { active.current = false; controller.current?.abort(); };
  }, []);
  const begin = () => { if (busyRef.current) return false; busyRef.current = true; setBusy(true); return true; };
  const finish = () => { busyRef.current = false; if (active.current) { setBusy(false); onStateChange?.("idle"); } };
  const runBriefing = async () => {
    if (!begin()) return;
    const assistant = chatMessage("assistant"); append(assistant); onStateChange?.("thinking");
    try { const result = await protectedPost("/briefing/run"); if (active.current) patch(assistant.id, { content: result.text }); }
    catch { if (active.current) patch(assistant.id, { content: "I couldn't compile the briefing just now, Sir." }); }
    finally { finish(); }
  };
  useBriefingSchedule(runBriefing, busy);
  const send = async (text) => {
    let content = (text ?? input).trim();
    if ((!content && !files.length) || !begin()) return;
    if (files.length) content = `${content} [attached: ${files.map((file) => file.name).join(", ")}]`.trim();
    setInput(""); setFiles([]);
    const user = chatMessage("user", content), assistant = chatMessage("assistant"); append(user, assistant);
    controller.current = new AbortController();
    try {
      const image = content.match(IMAGE_RE);
      if (image) { onStateChange?.("executing"); await renderImage(image[1].trim(), workspace, assistant, patch); return; }
      const macro = await findCommand(content);
      if (!active.current) return;
      if (macro && macro.action_type !== "prompt") {
        onStateChange?.("executing");
        const response = await protectedPost(`/commands/${macro.id}/run`);
        if (active.current) patch(assistant.id, { content: response.result.output || "Executed, Sir." });
        return;
      }
      await streamAnswer({ history: [...messages, user], macro, assistant, webMode, caps, patch, delta,
        state: onStateChange, signal: controller.current.signal, sessionId });
    } catch (error) { if (active.current && error.name !== "AbortError") patch(assistant.id, { content: `Request failed: ${error.message}` }); }
    finally { finish(); }
  };
  return { messages, input, setInput, files, setFiles, busy, webMode, setWebMode, caps, capabilityError, send, runBriefing };
}
